import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { integrationService } from '../../services/integrationService';
import NavigationSidebar from '../../components/ui/NavigationSidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import PlatformCard from './components/PlatformCard';
import ConfigurationPanel from './components/ConfigurationPanel';
import MonitoringDashboard from './components/MonitoringDashboard';
import { RefreshCw, Plus, Settings, AlertCircle } from 'lucide-react';

const SocialMediaIntegrationHub = () => {
  const { user } = useAuth();
  const [platforms, setPlatforms] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [aggregatedMetrics, setAggregatedMetrics] = useState({
    totalConnections: 0,
    activeDataFlows: 0,
    errorCount: 0,
    totalDataPoints: 0
  });

  useEffect(() => {
    if (user?.id) {
      loadPlatforms();
    }
  }, [user]);

  const loadPlatforms = async () => {
    try {
      setLoading(true);
      const data = await integrationService?.getUserIntegrations(user?.id);
      
      // Add mock platform data with status and metrics
      const platformsWithMetrics = [
        {
          id: 'facebook',
          name: 'Facebook',
          icon: '📘',
          status: 'connected',
          dataFlowRate: '125 posts/hr',
          lastSync: new Date(Date.now() - 1800000)?.toISOString(),
          health: 'healthy',
          apiUsage: 45,
          dataTypes: ['posts', 'comments', 'reactions', 'shares']
        },
        {
          id: 'linkedin',
          name: 'LinkedIn',
          icon: '💼',
          status: 'connected',
          dataFlowRate: '78 posts/hr',
          lastSync: new Date(Date.now() - 3600000)?.toISOString(),
          health: 'healthy',
          apiUsage: 32,
          dataTypes: ['posts', 'comments', 'reactions']
        },
        {
          id: 'twitter',
          name: 'Twitter/X',
          icon: '🐦',
          status: 'error',
          dataFlowRate: '0 tweets/hr',
          lastSync: new Date(Date.now() - 7200000)?.toISOString(),
          health: 'error',
          apiUsage: 0,
          dataTypes: ['tweets', 'mentions', 'replies', 'retweets'],
          error: 'Authentication token expired'
        },
        {
          id: 'reddit',
          name: 'Reddit',
          icon: '🤖',
          status: 'connected',
          dataFlowRate: '203 posts/hr',
          lastSync: new Date(Date.now() - 900000)?.toISOString(),
          health: 'healthy',
          apiUsage: 67,
          dataTypes: ['posts', 'comments', 'upvotes']
        },
        {
          id: 'instagram',
          name: 'Instagram',
          icon: '📷',
          status: 'warning',
          dataFlowRate: '45 posts/hr',
          lastSync: new Date(Date.now() - 5400000)?.toISOString(),
          health: 'warning',
          apiUsage: 89,
          dataTypes: ['posts', 'comments', 'stories'],
          warning: 'Approaching rate limit'
        },
        {
          id: 'google-reviews',
          name: 'Google Reviews',
          icon: '⭐',
          status: 'disconnected',
          dataFlowRate: '0 reviews/hr',
          lastSync: null,
          health: 'inactive',
          apiUsage: 0,
          dataTypes: ['reviews', 'ratings', 'responses']
        }
      ];

      setPlatforms(platformsWithMetrics);
      
      // Calculate aggregated metrics
      const metrics = {
        totalConnections: platformsWithMetrics?.filter(p => p?.status === 'connected')?.length,
        activeDataFlows: platformsWithMetrics?.filter(p => p?.health === 'healthy')?.length,
        errorCount: platformsWithMetrics?.filter(p => p?.status === 'error')?.length,
        totalDataPoints: platformsWithMetrics?.reduce((sum, p) => {
          const rate = parseInt(p?.dataFlowRate) || 0;
          return sum + rate;
        }, 0)
      };
      
      setAggregatedMetrics(metrics);
    } catch (error) {
      console.error('Error loading platforms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlatformSelect = (platform) => {
    setSelectedPlatform(platform);
    setShowConfig(true);
  };

  const handleConnect = async (platformId) => {
    try {
      // Simulate connection process
      setPlatforms(prev => prev?.map(p => 
        p?.id === platformId 
          ? { ...p, status: 'connected', health: 'healthy' }
          : p
      ));
    } catch (error) {
      console.error('Error connecting platform:', error);
    }
  };

  const handleDisconnect = async (platformId) => {
    try {
      setPlatforms(prev => prev?.map(p => 
        p?.id === platformId 
          ? { ...p, status: 'disconnected', health: 'inactive', dataFlowRate: '0/hr' }
          : p
      ));
    } catch (error) {
      console.error('Error disconnecting platform:', error);
    }
  };

  const breadcrumbs = [
    { label: 'Dashboard', path: '/live-dashboard' },
    { label: 'Social Media Integration Hub', path: '/social-media-integration-hub' }
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      <NavigationSidebar isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <BreadcrumbTrail items={breadcrumbs} />
          <div className="flex items-center justify-between mt-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Social Media Integration Hub</h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage platform connections and data collection settings
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadPlatforms}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Platform
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto p-6">
          {/* Aggregated Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Connections</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {aggregatedMetrics?.totalConnections}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Settings className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Data Flows</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {aggregatedMetrics?.activeDataFlows}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <RefreshCw className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Error Count</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">
                    {aggregatedMetrics?.errorCount}
                  </p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Data Points/Hour</p>
                  <p className="text-2xl font-bold text-purple-600 mt-1">
                    {aggregatedMetrics?.totalDataPoints}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">📊</span>
                </div>
              </div>
            </div>
          </div>

          {/* Platform Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {platforms?.map((platform) => (
              <PlatformCard
                key={platform?.id}
                platform={platform}
                onSelect={() => handlePlatformSelect(platform)}
                onConnect={() => handleConnect(platform?.id)}
                onDisconnect={() => handleDisconnect(platform?.id)}
              />
            ))}
          </div>

          {/* Monitoring Dashboard */}
          <MonitoringDashboard platforms={platforms} />
        </main>
      </div>
      {/* Configuration Panel */}
      {showConfig && selectedPlatform && (
        <ConfigurationPanel
          platform={selectedPlatform}
          onClose={() => {
            setShowConfig(false);
            setSelectedPlatform(null);
          }}
          onSave={(config) => {
            console.log('Saving config:', config);
            setShowConfig(false);
            setSelectedPlatform(null);
          }}
        />
      )}
    </div>
  );
};

export default SocialMediaIntegrationHub;